import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Context } from "../../store/AppContext";
import Head from "../Head";
import CotizacionFilters from "./CotizacionFilters";
import CotizacionStatusBadge from "./CotizacionStatusBadge";
import { quoteRepository } from "../../Helper/Cotizacion/quoteRepository";
import { getCurrentSeller, isAdminSeller } from "../../Helper/Cotizacion/currentUserAdapter";
import { formatCLP } from "../../Helper/Cotizacion/currencyUtils";
import { downloadQuotePdf } from "../../Helper/Cotizacion/quotePdf";

const emptyFilters = {
	number: "",
	cliente: "",
	rut: "",
	empresa: "",
	fecha: "",
	status: "",
};

const CotizacionListPage = () => {
	const { store } = useContext(Context);
	const navigate = useNavigate();
	const seller = useMemo(() => getCurrentSeller(store), [store.usuarioActual]);
	const [quotes, setQuotes] = useState([]);
	const [filters, setFilters] = useState(emptyFilters);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (store.usuarioActual == null && store.token == null) {
			const timer = setTimeout(() => navigate("/"), 200);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [store.usuarioActual, store.token, navigate]);

	useEffect(() => {
		let active = true;
		const load = async () => {
			setLoading(true);
			const data = await quoteRepository.list();
			if (active) {
				setQuotes(data);
				setLoading(false);
			}
		};
		load();
		return () => {
			active = false;
		};
	}, []);

	const visibleQuotes = useMemo(() => {
		const admin = isAdminSeller(seller);
		return quotes
			.filter((quote) => {
				if (!admin && String(quote.sellerId) !== String(seller.id)) {
					// Seeds demo: un usuario normal no autenticado como seed no las ve;
					// si el sellerId no coincide, se ocultan (simulación futura).
					return false;
				}
				if (filters.number && !(quote.number || "").toLowerCase().includes(filters.number.toLowerCase())) {
					return false;
				}
				if (
					filters.cliente &&
					!(quote.client?.nombre || "").toLowerCase().includes(filters.cliente.toLowerCase())
				) {
					return false;
				}
				if (
					filters.rut &&
					!(quote.client?.rut || "").toLowerCase().includes(filters.rut.toLowerCase())
				) {
					return false;
				}
				if (
					filters.empresa &&
					!(
						quote.issuerSnapshot?.nombreFantasia ||
						quote.issuerSnapshot?.nombreLegal ||
						""
					)
						.toLowerCase()
						.includes(filters.empresa.toLowerCase())
				) {
					return false;
				}
				if (filters.status && quote.status !== filters.status) return false;
				if (filters.fecha) {
					const dateSource = quote.issuedAt || quote.createdAt || "";
					if (!dateSource.startsWith(filters.fecha)) return false;
				}
				return true;
			})
			.sort(
				(a, b) =>
					new Date(b.updatedAt || b.createdAt).getTime() -
					new Date(a.updatedAt || a.createdAt).getTime()
			);
	}, [quotes, filters, seller]);

	return (
		<div className="no-print">
			<Head contenido={["Cotizaciones", "Mockup local"]} />
			<div className="grid-container fluid" style={{ padding: "0 16px 32px" }}>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						flexWrap: "wrap",
						gap: "12px",
						marginBottom: "16px",
					}}
				>
					<div>
						<h3 style={{ marginBottom: "4px" }}>Cotizaciones</h3>
						<p style={{ marginBottom: 0, color: "#4a5568" }}>
							{isAdminSeller(seller)
								? "Vista administrador: se muestran todas las cotizaciones simuladas."
								: "Solo se muestran tus cotizaciones (simulación)."}
						</p>
					</div>
					<Link to="/cotizacion/nueva" className="button primary" style={{ marginBottom: 0 }}>
						Nueva cotización
					</Link>
				</div>

				<CotizacionFilters filters={filters} onChange={setFilters} />

				{loading ? (
					<p>Cargando cotizaciones...</p>
				) : (
					<div style={{ overflowX: "auto" }}>
						<table className="stack hover">
							<thead>
								<tr>
									<th>Número</th>
									<th>Cliente</th>
									<th>RUT</th>
									<th>Empresa emisora</th>
									<th>Fecha</th>
									<th>Estado</th>
									<th>Total</th>
									<th>Acciones</th>
								</tr>
							</thead>
							<tbody>
								{visibleQuotes.length === 0 ? (
									<tr>
										<td colSpan={8}>No hay cotizaciones para los filtros actuales.</td>
									</tr>
								) : (
									visibleQuotes.map((quote) => (
										<tr key={quote.id}>
											<td>{quote.number || "—"}</td>
											<td>{quote.client?.nombre || "—"}</td>
											<td>{quote.client?.rut || "—"}</td>
											<td>
												{quote.issuerSnapshot?.nombreFantasia ||
													quote.issuerSnapshot?.nombreLegal ||
													"—"}
											</td>
											<td>
												{(quote.issuedAt || quote.createdAt || "")
													.slice(0, 10)
													.split("-")
													.reverse()
													.join("/") || "—"}
											</td>
											<td>
												<CotizacionStatusBadge status={quote.status} />
											</td>
											<td>{formatCLP(quote.grandTotal || 0)}</td>
											<td>
												<div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
													<Link
														to={`/cotizacion/ver/${quote.id}`}
														className="button tiny hollow"
														style={{ marginBottom: 0 }}
													>
														Ver
													</Link>
													{quote.status === "borrador" ? (
														<Link
															to={`/cotizacion/editar/${quote.id}`}
															className="button tiny hollow secondary"
															style={{ marginBottom: 0 }}
														>
															Editar
														</Link>
													) : null}
													<button
														type="button"
														className="button tiny"
														style={{ marginBottom: 0 }}
														onClick={() => downloadQuotePdf(quote)}
													>
														PDF
													</button>
												</div>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	);
};

export default CotizacionListPage;
